/* eslint-disable indent */
/**
 * Floating panel with transactions
 */
import { useEffect } from 'react';
import { Network } from '@kattana-io/common';
import { useNetwork } from 'wagmi';
import Text from '../../atoms/Text';
import {
  IIntermediateTransaction,
  transactionStatusType,
} from '../../reducers/transactions';

import './styles.scss';

interface IFloatingStatusBarProps {
  transactions: IIntermediateTransaction[];
  onClose(): void;
}

const getStatus = (
  transactionsLength: number,
  completedLength: number,
  failedLength: number
): transactionStatusType => {
  if (failedLength > 0) {
    return 'failed';
  }
  if (transactionsLength === completedLength) {
    return 'success';
  }
  return 'pending';
};

const getDataForStatus = (transactions: IIntermediateTransaction[]) => {
  const completedTransactions = transactions
    .filter((tx) => tx.transaction?.status === 'success')
    .map((tx) => tx.tx);
  const failedTransactions = transactions
    .filter((tx) => tx?.transaction?.status === 'failed')
    .map((tx) => tx.tx);
  const hasSwapIdx = transactions.findIndex((tx) => tx.trading);

  const status = getStatus(
    transactions.length,
    completedTransactions.length,
    failedTransactions.length
  );

  const meta = {
    status,
    transactionsNum:
      status === 'failed'
        ? failedTransactions.length
        : completedTransactions.length,
    errorMessage: null,
    hasSwap: hasSwapIdx !== -1,
    icons: {
      pending: 'icon-refresh-double',
      success: 'icon-check-circle',
      failed: 'icon-alert-triangle',
    },
  };

  switch (status) {
    case 'pending':
      return {
        ...meta,
        title: 'Processing transaction ...',
        description:
          meta.transactionsNum > 1
            ? 'transactions processed'
            : 'transaction processed',
      };
    case 'success':
      return {
        ...meta,
        title: 'Transactions processed',
        description:
          meta.transactionsNum > 1
            ? 'transactions successful'
            : 'transaction successful',
      };
    case 'failed':
      return {
        ...meta,
        title: 'Something went wrong',
        description:
          meta.transactionsNum > 1
            ? 'transactions were stopped'
            : 'transaction were stopped',
        errorMessage:
          '* Please go to the Notifications to check additional information about that failed.',
      };
  }
};

const formatTransactionTx = (address: string) =>
  `${address.slice(0, 10)}...${address.slice(address.length - 10)}`;

const FloatingStatusBar = ({
  transactions,
  onClose,
}: IFloatingStatusBarProps) => {
  const { chain } = useNetwork();
  const txUrl = Network.fromChainId(chain?.id)?.getScan() || '';

  const onCopy = (address: string) =>
    navigator.clipboard
      .writeText(address)
      .then(() => {
        console.log('Copied to clipboard');
      })
      .catch((err) => {
        console.warn('Could not copy to clipboard', err);
      });

  const onScan = (tx: string) => window.open(`${txUrl}${tx}`);

  const data = getDataForStatus(transactions);

  useEffect(() => {
    let timer: number | undefined;

    if (data.status === 'failed' || data.status === 'success') {
      timer = window.setTimeout(() => onClose(), 7000);
    }

    return () => window.clearTimeout(timer);
  }, [transactions.length, data.status]);

  return (
    <div className='floating_status'>
      <div className='floating_status__leave icon-close' onClick={onClose} />
      <Text size={16} className='text--weight-500 text--grey-200'>
        {data.title}
      </Text>
      <div className='floating_status__description'>
        {data.hasSwap && (
          <Text size={14} className='text--weight-500 text--grey-400'>
            Swapping tokens
          </Text>
        )}
        <Text
          size={14}
          className={`text--weight-500 text--grey-400 floating_status__description--${data.status}`}
        >
          {data.transactionsNum}&nbsp;/&nbsp;{transactions.length}
          &nbsp;
          {data.description}
        </Text>
      </div>
      <div className='floating_status__bars'>
        {transactions.map((tx) => (
          <div
            key={tx.tx}
            className={`floating_status__bars__bar floating_status__bars__bar--${tx.transaction?.status}`}
          >
            <div
              className={`floating_status__bars__bar__icon ${
                data.icons[tx.transaction?.status || 'pending']
              }`}
            />
            <Text size={14} className='text--weight-400 text--grey-400'>
              {formatTransactionTx(tx.tx)}
            </Text>
            {tx.transaction?.status === 'pending' &&
              !!tx.transaction?.timeToComplete && (
                <div
                  className='floating_status__bars__bar__indicator'
                  style={{
                    animationDuration: `${tx.transaction?.timeToComplete}ms`,
                  }}
                />
              )}
            {tx.transaction?.status === 'success' && (
              <div
                className='floating_status__bars__bar__icon floating_status__bars__bar__scan_btn icon-top-right-square'
                onClick={() => onScan(tx.tx)}
              />
            )}
            <div
              className='floating_status__bars__bar__icon floating_status__bars__bar__copy_btn icon-copy'
              onClick={() => onCopy(tx.tx)}
            />
          </div>
        ))}
      </div>
      {data.errorMessage && (
        <Text size={12} className='floating_status__error_message'>
          {data.errorMessage}
        </Text>
      )}
    </div>
  );
};

export default FloatingStatusBar;
